/**
 * Pagina per visualizzare tutti i video dalla tabella VideoForMake
 */
import React, { useState, useEffect } from "react";
import { getWebVideoList, getVideoStreamUrl, VideoItem } from "../api/videoWebApi";
import { getAgenciesForSelect } from "../api/agencyApi";
import PageHeader from "../components/PageHeader";
import { useNotification } from "../context/NotificationContext";

const VideosPage: React.FC = () => {
  const { addNotification } = useNotification();
  
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [allVideos, setAllVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState<string>("");
  const [tenantOptions, setTenantOptions] = useState<{ value: string; label: string }[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 12,
    totalPages: 0
  });
  
  // Paginazione server-side
  const [currentPage, setCurrentPage] = useState(1);

  const loadVideos = async (page = 1, tenant = selectedTenant) => {
    try {
      setLoading(true);
      const response = await getWebVideoList(
        page,
        pagination.limit,
        tenant || undefined,
        "createdAt",
        "desc"
      );
      
      if (response.success) {
        setVideos(response.data);
        setPagination(response.pagination);
        setCurrentPage(page);
        
        console.log(`✅ Caricati ${response.data.length} video (pagina ${page}/${response.pagination.totalPages})`);
      } else {
        addNotification("error", response.error || "Errore nel caricamento dei video");
        setVideos([]);
      }
    } catch (error: any) {
      console.error("Errore nel caricamento video:", error);
      addNotification("error", "Errore nel caricamento dei video");
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenantOptions = async () => {
    try {
      const options = await getAgenciesForSelect();
      setTenantOptions([
        { value: "", label: "Tutti i tenant" },
        ...options
      ]);
    } catch (error) {
      console.error("Errore nel caricamento opzioni tenant:", error);
    }
  };

  useEffect(() => {
    loadVideos();
    loadTenantOptions();
  }, []);

  // Ricarica quando cambia il filtro tenant
  const handleTenantChange = (newTenant: string) => {
    setSelectedTenant(newTenant);
    loadVideos(1, newTenant); // Riparti dalla pagina 1
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("it-IT", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleVideoError = (videoId: string) => {
    console.error(`Errore nel caricamento del video: ${videoId}`);
    addNotification("error", "Errore nel caricamento del video");
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      loadVideos(page, selectedTenant);
    }
  };

  if (loading && currentPage === 1) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="flex flex-col items-center space-y-3">
          <svg
            className="animate-spin h-8 w-8 text-purple-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-600">Caricamento video...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Video VideoForMake"
        subtitle="Visualizza tutti i video dalla tabella VideoForMake con possibilità di riproduzione"
      />

      {/* Statistiche e Filtri */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">🎥</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Totale Video</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">📄</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Pagina</p>
              <p className="text-2xl font-bold text-gray-900">{currentPage} / {pagination.totalPages || 1}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className="text-2xl">🔍</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Video Pagina</p>
              <p className="text-2xl font-bold text-gray-900">{videos.length}</p>
            </div>
          </div>
        </div>

        {/* Filtro Tenant */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtra per Tenant
          </label>
          <select
            value={selectedTenant}
            onChange={(e) => handleTenantChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            {tenantOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Indicatore di caricamento per cambio pagina */}
      {loading && (
        <div className="text-center py-4">
          <div className="inline-flex items-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-purple-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-gray-600">Caricamento...</span>
          </div>
        </div>
      )}

      {/* Griglia Video */}
      {videos.length === 0 && !loading ? (
        <div className="bg-white rounded-lg border border-gray-200 text-center py-12">
          <span className="text-4xl mb-4 block">🎥</span>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nessun video trovato
          </h3>
          <p className="text-gray-600">
            {selectedTenant 
              ? `Non ci sono video per il tenant "${selectedTenant}".`
              : "Non sono presenti video nella tabella VideoForMake."
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {videos.map((video) => (
            <div 
              key={video._id} 
              className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              {/* Video Player */}
              <div className="aspect-video bg-gray-900 rounded-t-lg overflow-hidden relative">
                <video
                  controls
                  className="w-full h-full object-cover"
                  poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjMzc0MTUxIi8+Cjx0ZXh0IHg9IjUwIiB5PSI1NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzlDQTNBRiIgZm9udC1zaXplPSIzMiI+8J+UtTwvdGV4dD4KPHN2Zz4K"
                  onError={(e) => {
                    console.error(`Errore video ${video._id}:`, e);
                    console.error(`URL: ${getVideoStreamUrl(video._id)}`);
                    handleVideoError(video._id);
                  }}
                  onLoadStart={() => console.log(`Loading video ${video._id}: ${getVideoStreamUrl(video._id)}`)}
                  preload="metadata"
                  crossOrigin="anonymous"
                >
                  <source 
                    src={getVideoStreamUrl(video._id)} 
                    type="video/mp4" 
                  />
                  Il tuo browser non supporta la riproduzione video.
                </video>
              </div>

              {/* Informazioni Video */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 text-sm mb-1">
                      Video ID: {video._id.slice(0, 8)}...
                    </h3>
                    <p className="text-xs text-gray-500">
                      {formatDate(video.createdAt)}
                    </p>
                  </div>
                  
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {video.tenant}
                  </span>
                </div>

                {/* Caption */}
                {video.caption && (
                  <div className="mb-3">
                    <p className="text-sm text-gray-700 line-clamp-3">
                      {video.caption}
                    </p>
                  </div>
                )}

                {/* Metadati */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center">
                    <span className="mr-1">🗂️</span>
                    Video File
                  </span>
                </div>
                
                <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center">
                    <span className="mr-1">📅</span>
                    {new Date(video.updatedAt).toLocaleDateString("it-IT")}
                  </span>
                  
                  <span className="flex items-center">
                    <span className="mr-1">🎬</span>
                    VideoForMake
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paginazione */}
      {pagination.totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1 || loading}
              className="relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Precedente
            </button>
            
            {/* Mostra max 5 pagine */}
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const startPage = Math.max(1, currentPage - 2);
              const page = startPage + i;
              if (page > pagination.totalPages) return null;
              
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  disabled={loading}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium disabled:opacity-50 ${
                    page === currentPage
                      ? "z-10 bg-purple-50 border-purple-500 text-purple-600"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= pagination.totalPages || loading}
              className="relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Successiva
            </button>
          </nav>
          
          <div className="ml-4 text-sm text-gray-700">
            Pagina {currentPage} di {pagination.totalPages} ({pagination.total} video totali)
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosPage;